import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Switch, Divider, Alert, Space, Button } from 'antd';
import { HomeOutlined, GlobalOutlined, PlusOutlined, DeleteOutlined } from '@ant-design/icons';

/**
 * CreateZoneModal - FINAL FIXED VERSION
 * ✅ Sends data in format backend expects: { name, isCapitalZone, form: { name, polygon } }
 * ✅ Handles both 'lng' (from map) and 'long' (backend format)
 */
const CreateZoneModal = ({ 
  visible, 
  polygon = null,
  onCancel, 
  onCreate,
  tenantName = "jordan"
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [polygonCoords, setPolygonCoords] = useState([]);
  const [hasBoundaries, setHasBoundaries] = useState(false);

  const getCapitalInfo = () => {
    const tenant = tenantName?.toLowerCase() || 'jordan';
    const capitalMap = {
      'jordan': { name: 'Amman', defaultCapital: true },
      'qatar': { name: 'Doha', defaultCapital: true },
    };
    return capitalMap[tenant] || { name: 'Capital', defaultCapital: false };
  };

  const capitalInfo = getCapitalInfo();

  useEffect(() => {
    if (visible) {
      form.resetFields();
      
      if (polygon && Array.isArray(polygon) && polygon.length > 0) {
        // ✅ Normalize coordinates: map drawing uses 'lng', backend needs 'long'
        const normalized = polygon.map(coord => ({
          lat: coord.lat || 0,
          long: coord.long || coord.lng || 0  // Support both formats
        }));
        
        console.log('📍 Normalized polygon from map:', normalized);
        setPolygonCoords(normalized);
        setHasBoundaries(true);
        form.setFieldsValue({ hasPolygon: true });
      } else {
        setPolygonCoords([]);
        setHasBoundaries(false);
        form.setFieldsValue({ hasPolygon: false });
      }

      form.setFieldsValue({
        isCapitalZone: capitalInfo.defaultCapital
      });
    }
  }, [visible, polygon, form, capitalInfo.defaultCapital]);

  const handleAddCoordinate = () => {
    setPolygonCoords([...polygonCoords, { lat: 0, long: 0 }]);
  };

  const handleRemoveCoordinate = (index) => {
    setPolygonCoords(polygonCoords.filter((_, i) => i !== index));
  };

  const handleCoordinateChange = (index, field, value) => {
    const updated = [...polygonCoords];
    updated[index] = { ...updated[index], [field]: parseFloat(value) || 0 };
    setPolygonCoords(updated);
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      
      console.log('📝 Form values:', values);
      console.log('📍 Polygon coords:', polygonCoords);

      if (values.hasPolygon && polygonCoords.length < 3) {
        Modal.error({
          title: 'Invalid Polygon',
          content: 'A polygon must have at least 3 coordinate points.'
        });
        setLoading(false);
        return;
      }

      // ✅ Normalize all coordinates to 'long' format
      const normalizedPolygon = polygonCoords.map(coord => ({
        lat: coord.lat,
        long: coord.long || coord.lng || 0
      }));

      if (values.hasPolygon && normalizedPolygon.length > 0) {
        const invalidCoords = normalizedPolygon.some(coord => 
          isNaN(coord.lat) || isNaN(coord.long) || 
          coord.lat === 0 || coord.long === 0
        );
        
        if (invalidCoords) {
          Modal.error({
            title: 'Invalid Coordinates',
            content: 'Please ensure all coordinates have valid latitude and longitude values.'
          });
          setLoading(false);
          return;
        }
      }

      // ✅ CRITICAL: Send in format backend expects
      const zoneData = {
        name: values.zoneName.trim(),
        isCapitalZone: values.isCapitalZone || false,
        form: {
          name: values.zoneName.trim(),
          polygon: values.hasPolygon ? normalizedPolygon : []
        }
      };

      console.log('✅ Sending to backend:', JSON.stringify(zoneData, null, 2));

      await onCreate(zoneData);
      
      form.resetFields();
      setPolygonCoords([]);
      setLoading(false);

    } catch (error) {
      console.error('❌ Form validation failed:', error);
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setPolygonCoords([]);
    setHasBoundaries(false);
    onCancel();
  };

  const handleBoundariesToggle = (checked) => {
    setHasBoundaries(checked);
    form.setFieldsValue({ hasPolygon: checked });
  };

  const handleJsonPaste = (jsonText) => {
    try {
      const parsed = JSON.parse(jsonText);
      if (Array.isArray(parsed)) {
        const normalized = parsed.map(coord => ({
          lat: coord.lat || coord.latitude || 0,
          long: coord.long || coord.lng || coord.longitude || 0
        }));
        
        console.log('📋 Loaded from JSON:', normalized);
        setPolygonCoords(normalized);
        
        Modal.success({
          title: 'Polygon Loaded!',
          content: `Successfully loaded ${normalized.length} coordinate points.`
        });
      }
    } catch (error) {
      console.error('Invalid JSON:', error);
    }
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <PlusOutlined style={{ color: '#1890ff' }} />
          <span>Create New Zone</span>
        </div>
      }
      visible={visible}
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      okText="Create Zone"
      cancelText="Cancel"
      confirmLoading={loading}
      width={800}
      maskClosable={false}
    >
      <Alert
        message={`Tenant: ${tenantName?.toUpperCase() || 'JORDAN'}`}
        description={`Capital city: ${capitalInfo.name}`}
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          isCapitalZone: capitalInfo.defaultCapital,
          hasPolygon: polygon && polygon.length > 0
        }}
      >
        <Form.Item
          label="Zone Name"
          name="zoneName"
          rules={[
            { required: true, message: 'Please enter zone name' },
            { min: 2, message: 'Zone name must be at least 2 characters' }
          ]}
        >
          <Input placeholder="e.g., Jubaiha, Abdoun" size="large" />
        </Form.Item>

        <Divider />

        <Form.Item
          label={
            <Space>
              <span>Is this zone inside the capital city?</span>
              {capitalInfo.defaultCapital ? <HomeOutlined /> : <GlobalOutlined />}
            </Space>
          }
          name="isCapitalZone"
          valuePropName="checked"
        >
          <Switch
            checkedChildren={`Inside ${capitalInfo.name}`}
            unCheckedChildren="Outside Capital"
          />
        </Form.Item>

        <Alert
          message={
            form.getFieldValue('isCapitalZone')
              ? `✅ This zone is INSIDE ${capitalInfo.name} (Capital)`
              : `❌ This zone is OUTSIDE the capital city`
          }
          type={form.getFieldValue('isCapitalZone') ? 'success' : 'warning'}
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Divider />

        <div style={{ marginBottom: 16 }}>
          <h3>Polygon Boundaries (Coordinates)</h3>
          <p style={{ color: '#666', fontSize: '13px' }}>
            Define the geographic boundaries. Paste JSON, enter manually, or draw on map.
          </p>
        </div>

        <Form.Item name="hasPolygon" valuePropName="checked">
          <Switch
            checked={hasBoundaries}
            onChange={handleBoundariesToggle}
            checkedChildren="Has Boundaries"
            unCheckedChildren="No Boundaries Yet"
          />
        </Form.Item>

        {hasBoundaries && (
          <>
            <Alert
              message="Polygon Coordinates"
              description={`Enter at least 3 coordinate pairs. Current points: ${polygonCoords.length}`}
              type="info"
              style={{ marginBottom: 16 }}
            />

            <div style={{ marginBottom: 16 }}>
              <Button
                type="dashed"
                onClick={() => {
                  const el = document.getElementById('polygon-json-input');
                  if (el) el.style.display = el.style.display === 'none' ? 'block' : 'none';
                }}
                block
                style={{ marginBottom: 8 }}
              >
                📋 Paste Polygon JSON
              </Button>
              
              <div id="polygon-json-input" style={{ display: 'none' }}>
                <Input.TextArea
                  rows={6}
                  placeholder={`Paste JSON (supports both lng and long):
[
  {"lat": 32.0156, "long": 35.8664},
  {"lat": 32.0189, "long": 35.8712},
  {"lat": 32.0134, "long": 35.8712}
]`}
                  onChange={(e) => handleJsonPaste(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: '12px' }}
                />
                <div style={{ marginTop: 8, fontSize: '12px', color: '#666' }}>
                  💡 Supports: {`{lat, long}`}, {`{lat, lng}`}, {`{latitude, longitude}`}
                </div>
              </div>
            </div>

            <div style={{ 
              maxHeight: '300px', 
              overflowY: 'auto',
              border: '1px solid #d9d9d9',
              borderRadius: '4px',
              padding: '12px',
              marginBottom: '16px'
            }}>
              {polygonCoords.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                  No coordinates yet. Paste JSON or click "Add Coordinate" below.
                </div>
              ) : (
                polygonCoords.map((coord, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: '8px',
                      marginBottom: '12px',
                      alignItems: 'center',
                      padding: '8px',
                      background: '#fafafa',
                      borderRadius: '4px'
                    }}
                  >
                    <span style={{ fontWeight: 'bold', minWidth: '30px' }}>
                      {index + 1}.
                    </span>
                    
                    <div style={{ flex: 1 }}>
                      <Input
                        addonBefore="Lat"
                        type="number"
                        step="0.000001"
                        value={coord.lat}
                        onChange={(e) => handleCoordinateChange(index, 'lat', e.target.value)}
                        placeholder="32.0156"
                      />
                    </div>
                    
                    <div style={{ flex: 1 }}>
                      <Input
                        addonBefore="Long"
                        type="number"
                        step="0.000001"
                        value={coord.long}
                        onChange={(e) => handleCoordinateChange(index, 'long', e.target.value)}
                        placeholder="35.8664"
                      />
                    </div>

                    <Button
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveCoordinate(index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))
              )}
            </div>

            <Space style={{ width: '100%', marginBottom: 12 }}>
              <Button
                type="dashed"
                onClick={handleAddCoordinate}
                icon={<PlusOutlined />}
              >
                Add Coordinate Point
              </Button>
              
              {polygonCoords.length > 0 && (
                <Button
                  danger
                  onClick={() => {
                    Modal.confirm({
                      title: 'Clear all coordinates?',
                      content: 'This will remove all coordinate points.',
                      onOk: () => setPolygonCoords([])
                    });
                  }}
                >
                  Clear All
                </Button>
              )}
            </Space>

            {polygonCoords.length > 0 && polygonCoords.length < 3 && (
              <Alert
                message={`⚠️ Need at least 3 points. Currently: ${polygonCoords.length}`}
                type="warning"
                showIcon
                style={{ marginTop: 12 }}
              />
            )}
          </>
        )}

        {!hasBoundaries && (
          <Alert
            message="Zone without boundaries"
            description="This zone will be created without geographic boundaries. You can add boundaries later."
            type="warning"
            showIcon
          />
        )}
      </Form>
    </Modal>
  );
};

export default CreateZoneModal;